import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';

const tools = [
  {
    name: 'Chat Tradicional (igual ao ChatGPT)',
    route: '/ai-text-generator',
    description: 'Gere textos, artigos, e-mails e conteúdos profissionais com IA.',
    icon: '✍️',
    gradient: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
    tags: ['Conteúdo', 'Escrita', 'IA'],
  },
  {
    name: 'Assistente do Estudante',
    route: '/student-chat',
    description: 'Assistente de estudos para tirar dúvidas e aprender mais rápido.',
    icon: '🎓',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    tags: ['Estudos', 'Chat', 'Aprendizado'],
  },
  {
    name: 'Assistente do Professor',
    route: '/teacher-chat',
    description: 'Ferramentas para professores criarem aulas e materiais didáticos.',
    icon: '📚',
    gradient: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',
    tags: ['Educação', 'Ensino', 'Materiais'],
  },
  {
    name: 'Ferramenta do Funcionário',
    route: '/employee-tool',
    description: 'Otimize tarefas corporativas, relatórios e comunicações internas.',
    icon: '💼',
    gradient: 'linear-gradient(135deg, #3b1f6e 0%, #7c3aed 100%)',
    tags: ['Corporativo', 'Produtividade', 'RH'],
  },
  {
    name: 'Ferramenta do Empreendedor',
    route: '/entrepreneur-tool',
    description: 'Planejamento de negócios, estratégias e análise de mercado com IA.',
    icon: '🚀',
    gradient: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)',
    tags: ['Negócios', 'Estratégia', 'Empreendedorismo'],
  },
  {
    name: 'Melhore sua Saúde',
    route: '/health-improvement',
    description: 'Dicas personalizadas de saúde, bem-estar e hábitos saudáveis.',
    icon: '❤️',
    gradient: 'linear-gradient(135deg, #881337 0%, #e11d48 100%)',
    tags: ['Saúde', 'Bem-estar', 'Hábitos'],
  },
  {
    name: 'Professor de Idiomas',
    route: '/language-coach',
    description: 'Aprenda e pratique idiomas com um professor de linguagem inteligente.',
    icon: '🌍',
    gradient: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
    tags: ['Idiomas', 'Prática', 'Conversação'],
  },
  {
    name: 'Solução de Ouro',
    route: '/golden-solution',
    description: 'Soluções estratégicas e criativas para desafios complexos.',
    icon: '⭐',
    gradient: 'linear-gradient(135deg, #713f12 0%, #d97706 100%)',
    tags: ['Estratégia', 'Soluções', 'Inovação'],
  },
  {
    name: 'Atendimento Premium',
    route: '/atendimento-premium',
    description: 'Fale com um especialista humano e receba orientação personalizada.',
    icon: '🎯',
    gradient: 'linear-gradient(135deg, #065f46 0%, #10a37f 100%)',
    tags: ['Suporte', 'Humano', 'Premium'],
  },
];

const ToolCard = ({ tool, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl border border-gray-100 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4"
        style={{ background: tool?.gradient }}
      >
        {tool?.icon}
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
        {tool?.name}
      </h3>
      <p className="text-sm text-gray-500 leading-relaxed mb-4">
        {tool?.description}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {tool?.tags?.map((tag) => (
          <span
            key={tag}
            className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 border border-gray-100"
          >
            {tag}
          </span>
        ))}
      </div>
      <div
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-2 rounded-lg transition-all duration-200 group-hover:opacity-90"
        style={{ background: tool?.gradient }}
      >
        Abrir ferramenta
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

const Portal = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await supabase?.auth?.signOut();
    } catch (e) {
      // silent
    }
    navigate('/dashboard');
  };

  const handleManageSubscription = () => {
    window.location.href = "https://billing.stripe.com/p/login/bJe5kEd1kfjOeCZfpN9AA00";
  };

  return (
    <>
      <Helmet>
        <title>Portal de Ferramentas - AI7X7</title>
        <meta name="description" content="Acesse todas as ferramentas de IA do AI7X7" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      <div
        className="min-h-screen"
        style={{ backgroundColor: '#F7F7F8', fontFamily: "'DM Sans', sans-serif" }}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Portal de Ferramentas</h1>
            <div className="flex items-center gap-3">
              <button
                onClick={handleManageSubscription}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-150"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Gerenciar Assinatura
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-150"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sair
              </button>
            </div>
          </div>
        </div>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {tools?.map((tool) => (
              <ToolCard
                key={tool?.route}
                tool={tool}
                onClick={() => navigate(tool?.route)}
              />
            ))}
          </div>
        </main>
      </div>
    </>
  );
};

export default Portal;
